import type {
  ConstituencyStatus, LobbyScheduleDto, MeetingValidity, SaveLobbyScheduleRequest,
  ScheduleAttendeeDto, ScheduleConflictDto, ScheduleLegislatorRowDto, ScheduleMeetingDto,
} from '../../../types/lobbyDay'

export const SLOT_MINUTES = 30
export const DAY_START_MINUTES = 8 * 60
export const DAY_END_MINUTES = 17 * 60

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
}

export function slotLabels(): string[] {
  const labels: string[] = []
  for (let t = DAY_START_MINUTES; t < DAY_END_MINUTES; t += SLOT_MINUTES) {
    const h24 = Math.floor(t / 60)
    const m = t % 60
    const h12 = ((h24 + 11) % 12) + 1
    labels.push(`${h12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`)
  }
  return labels
}

/** Format an "HH:mm:ss" time as a 12-hour clock label ("8:00 AM"), matching the grid axis. */
export function formatSlotTime(time: string): string {
  const total = timeToMinutes(time)
  const h24 = Math.floor(total / 60)
  const m = total % 60
  const h12 = ((h24 + 11) % 12) + 1
  return `${h12}:${String(m).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`
}

export function slotIndex(time: string): number {
  return Math.floor((timeToMinutes(time) - DAY_START_MINUTES) / SLOT_MINUTES)
}

export function slotSpan(start: string, end: string): number {
  return Math.max(1, Math.ceil((timeToMinutes(end) - timeToMinutes(start)) / SLOT_MINUTES))
}

/** Client mirror of backend DistrictMatcher.Evaluate, driven by server-precomputed data.
 *  Keep in sync with Server.Features.LobbyDay.Services.LobbyScheduleLogic / DistrictMatcher. */
export function constituencyFor(
  attendee: ScheduleAttendeeDto,
  legislator: ScheduleLegislatorRowDto,
): ConstituencyStatus {
  if (attendee.constituentOfLegislatorIds.includes(legislator.legislatorId)) return 'Constituent'
  if (!attendee.hasVoterRecord) return 'Unknown'
  // District numbers collide across states: a present-but-different state is disqualifying
  // for every chamber except USSenate (mirrors DistrictMatcher.Evaluate's statesMatch check).
  if (legislator.chamber !== 'USSenate' && attendee.voterState && attendee.voterState !== legislator.state) {
    return 'NotConstituent'
  }
  const field =
    legislator.chamber === 'StateHouse' ? attendee.stateHouseDistrict :
    legislator.chamber === 'StateSenate' ? attendee.stateSenateDistrict :
    legislator.chamber === 'USHouse' ? attendee.congressionalDistrict :
    attendee.voterState
  return field == null || field === '' ? 'Unknown' : 'NotConstituent'
}

export function computeValidity(statuses: ConstituencyStatus[]): MeetingValidity {
  if (statuses.length === 0) return 'Empty'
  if (statuses.includes('Constituent')) return 'Valid'
  if (statuses.includes('Unknown')) return 'Unverified'
  return 'NoConstituent'
}

export function findConflicts(meetings: ScheduleMeetingDto[]): ScheduleConflictDto[] {
  const spans = meetings.flatMap((m) =>
    m.assignments.map((a) => ({
      meetingId: m.id, attendeeId: a.attendeeId, date: m.meetingDate,
      start: timeToMinutes(m.startTime), end: timeToMinutes(m.endTime),
    })))
  const conflicts: ScheduleConflictDto[] = []
  const byKey = new Map<string, typeof spans>()
  for (const s of spans) {
    const key = `${s.attendeeId}|${s.date}`
    if (!byKey.has(key)) byKey.set(key, [])
    byKey.get(key)!.push(s)
  }
  for (const group of byKey.values()) {
    const list = [...group].sort((a, b) => a.start - b.start)
    for (let i = 0; i < list.length; i++)
      for (let j = i + 1; j < list.length; j++) {
        if (list[j].start >= list[i].end) break
        conflicts.push({ attendeeId: list[i].attendeeId, meetingIdA: list[i].meetingId, meetingIdB: list[j].meetingId })
      }
  }
  return conflicts
}

export function meetingCounts(meetings: ScheduleMeetingDto[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const m of meetings)
    for (const a of m.assignments)
      counts.set(a.attendeeId, (counts.get(a.attendeeId) ?? 0) + 1)
  return counts
}

/** legislatorId -> set of attendeeIds who are that legislator's constituents.
 *  Reverse of ScheduleAttendeeDto.constituentOfLegislatorIds; drives the hover glow. */
export function constituentsByLegislator(attendees: ScheduleAttendeeDto[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>()
  for (const a of attendees)
    for (const legId of a.constituentOfLegislatorIds) {
      if (!map.has(legId)) map.set(legId, new Set())
      map.get(legId)!.add(a.attendeeId)
    }
  return map
}

export function loadColor(count: number): 'yellow' | 'green' | 'red' {
  if (count > 6) return 'red'
  if (count >= 3) return 'green'
  return 'yellow'
}

export const isNewId = (id: string) => id.startsWith('new:')

/** True when it is safe to navigate away: not dirty, or the user confirmed the discard.
 *  `confirm` is injected (window.confirm in the app) so this stays pure and testable. */
export function confirmLeaveIfDirty(isDirty: boolean, confirm: () => boolean): boolean {
  return !isDirty || confirm()
}

export function buildSaveRequest(
  server: LobbyScheduleDto,
  localMeetings: ScheduleMeetingDto[],
): SaveLobbyScheduleRequest {
  const localMeetingIds = new Set(localMeetings.map((m) => m.id))
  const localAssignmentIds = new Set(
    localMeetings.flatMap((m) => m.assignments.map((a) => a.assignmentId)))

  const deletedMeetingIds = server.meetings
    .filter((m) => !localMeetingIds.has(m.id))
    .map((m) => m.id)
  const deletedMeetingIdSet = new Set(deletedMeetingIds)

  // Assignments on deleted meetings are covered by the meeting cascade — don't double-report.
  const deletedAssignmentIds = server.meetings
    .filter((m) => !deletedMeetingIdSet.has(m.id))
    .flatMap((m) => m.assignments)
    .filter((a) => !localAssignmentIds.has(a.assignmentId))
    .map((a) => a.assignmentId)

  return {
    baseVersion: server.scheduleVersion,
    meetingUpserts: localMeetings.map((m) => ({
      id: isNewId(m.id) ? null : m.id,
      clientKey: isNewId(m.id) ? m.id : null,
      legislatorId: m.legislatorId,
      askId: m.askId,
      meetingDate: m.meetingDate,
      startTime: m.startTime,
      endTime: m.endTime,
      room: m.room,
      status: m.status,
      withWhom: m.withWhom,
      packetNotes: m.packetNotes,
      notes: m.notes,
    })),
    deletedMeetingIds,
    assignmentUpserts: localMeetings.flatMap((m) =>
      m.assignments
        .filter((a) => isNewId(a.assignmentId))
        .map((a) => isNewId(m.id)
          ? { meetingClientKey: m.id, attendeeId: a.attendeeId }
          : { meetingId: m.id, attendeeId: a.attendeeId })),
    deletedAssignmentIds,
  }
}
